import { useLanguage } from "@/context/language"
import { coworkAccountingReportTemplates, type AccountCardSelection } from "@/cowork/report-templates"
import { coworkReportCatalog, coworkReportCategories, type CoworkReportCategory } from "@/cowork/report-catalog"
import { createMemo, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

export type CoworkReportTemplate = (typeof coworkAccountingReportTemplates)[number]

export function CoworkReportTemplateCatalog(props: {
  onGenerate: (template: CoworkReportTemplate, accountCard?: AccountCardSelection) => void
  disabled?: boolean
  busyTemplateID?: string
  describedBy?: string
  accountCardNeedsPeriod?: boolean
  initialCountry?: string
}) {
  const language = useLanguage()
  const [filters, setFilters] = createStore({
    country: props.initialCountry ?? "",
    category: "" as CoworkReportCategory | "",
    search: "",
  })
  const templates = createMemo(() => {
    const search = filters.search.trim().toLowerCase()
    return coworkReportCatalog.filter((template) => {
      if (filters.category && template.category !== filters.category) return false
      if (filters.country && (template.country ?? "general") !== filters.country) return false
      const text = [
        language.t(template.titleKey),
        language.t(template.descriptionKey),
        language.t(coworkReportCategories[template.category]),
        ...(template.sourcesKey ? [language.t(template.sourcesKey)] : []),
        ...(template.kind === "preview"
          ? [language.t(template.outputsKey)]
          : [language.t(template.formatKey), ...(template.sourcesKey ? template.definition?.outputs ?? [] : [])]),
      ]
      return !search || text.join(" ").toLowerCase().includes(search)
    })
  })

  return (
    <div class="grid items-start gap-6 md:grid-cols-[200px_minmax(0,1fr)]">
      <aside
        class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5"
        aria-label={language.t("cowork.enterprise.reportTemplates.filters")}
      >
        <h2 class="text-[13px] font-semibold">{language.t("cowork.enterprise.reportTemplates.filters")}</h2>
        <div
          class="mt-5 flex flex-wrap gap-2 md:flex-col"
          role="group"
          aria-label={language.t("cowork.enterprise.reportTemplates.reportType")}
        >
          <button
            type="button"
            aria-pressed={filters.category === ""}
            class="rounded-md border border-v2-border-border-base px-3 py-2.5 text-left text-[12px] font-medium hover:bg-v2-background-bg-layer-02 aria-pressed:bg-v2-background-bg-contrast aria-pressed:text-v2-text-text-contrast"
            onClick={() => setFilters("category", "")}
          >
            {language.t("cowork.enterprise.reportTemplates.all")}
          </button>
          <For each={Object.entries(coworkReportCategories)}>
            {([category, label]) => (
              <button
                type="button"
                aria-pressed={filters.category === category}
                class="rounded-md border border-v2-border-border-base px-3 py-2.5 text-left text-[12px] font-medium hover:bg-v2-background-bg-layer-02 aria-pressed:bg-v2-background-bg-contrast aria-pressed:text-v2-text-text-contrast"
                onClick={() => setFilters("category", category as CoworkReportCategory)}
              >
                {language.t(label)}
              </button>
            )}
          </For>
        </div>
        <label class="mt-6 block border-t border-v2-border-border-base pt-5 text-[12px] font-medium">
          {language.t("cowork.enterprise.reportTemplates.country")}
          <select
            class="mt-3 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-2.5"
            value={filters.country}
            onChange={(event) => setFilters("country", event.currentTarget.value)}
          >
            <option value="">{language.t("cowork.enterprise.reportTemplates.allCountries")}</option>
            <option value="EE">{language.t("cowork.agent.settings.templates.format.estonia")}</option>
            <option value="general">{language.t("cowork.enterprise.reportTemplates.general")}</option>
          </select>
        </label>
      </aside>

      <section class="min-w-0" aria-label={language.t("cowork.agent.settings.templates.title")}>
        <label class="block text-[12px] font-medium">
          {language.t("cowork.enterprise.reports.searchLabel")}
          <input
            type="search"
            class="mt-2 w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-4 py-3 text-[13px]"
            placeholder={language.t("cowork.enterprise.reports.search")}
            value={filters.search}
            onInput={(event) => setFilters("search", event.currentTarget.value)}
          />
        </label>
        <p role="status" aria-live="polite" class="my-4 text-[12px] text-v2-text-text-muted">
          {language.t("cowork.enterprise.reportTemplates.results", { count: templates().length })}
        </p>
        <Show
          when={templates().length > 0}
          fallback={
            <p class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-6 text-[13px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.reportTemplates.empty")}
            </p>
          }
        >
          <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <For each={templates()}>
              {(template) => (
                <article class="flex min-h-48 flex-col rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <span class="rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-02 px-2 py-1 text-[10px] font-semibold text-v2-text-text-muted">
                      {language.t(
                        template.country === "EE"
                          ? "cowork.agent.settings.templates.format.estonia"
                          : "cowork.enterprise.reportTemplates.general",
                      )}
                    </span>
                    <Show when={template.kind === "preview"}>
                      <span class="rounded-md bg-v2-background-bg-layer-02 px-2 py-1 text-[10px] font-medium text-v2-text-text-muted">
                        {language.t("cowork.enterprise.reportTemplates.comingSoon")}
                      </span>
                    </Show>
                  </div>
                  <p class="mt-4 text-[11px] text-v2-text-text-faint">
                    {language.t(coworkReportCategories[template.category])}
                  </p>
                  <h2 class="mt-2 text-[14px] font-medium">{language.t(template.titleKey)}</h2>
                  <p class="mt-2 text-[12px] leading-5 text-v2-text-text-muted">
                    {language.t(template.descriptionKey)}
                  </p>
                  <div class="mt-auto pt-4">
                    <Show when={template.id === "account-card"}>
                      <Show when={props.accountCardNeedsPeriod}>
                        <p class="mb-3 text-[12px] text-v2-text-text-muted">
                          {language.t("cowork.agent.settings.template.accountCard.selectPeriod")}
                        </p>
                      </Show>
                    </Show>
                    {template.sourcesKey ? (
                      <dl class="mb-4 text-[11px] leading-5">
                        <dt class="font-medium">{language.t("cowork.enterprise.reportTemplates.sources")}</dt>
                        <dd class="mt-1 text-v2-text-text-muted">{language.t(template.sourcesKey)}</dd>
                        <dt class="mt-3 font-medium">{language.t("cowork.enterprise.reportTemplates.outputs")}</dt>
                        <dd class="mt-1 text-v2-text-text-muted">
                          {template.kind === "preview"
                            ? language.t(template.outputsKey)
                            : template.definition?.outputs.map((format) => format.toUpperCase()).join(" · ")}
                        </dd>
                      </dl>
                    ) : (
                      <Show when={template.formatKey !== "cowork.agent.settings.templates.format.estonia"}>
                        <p class="mb-4 text-[11px] text-v2-text-text-faint">{language.t(template.formatKey)}</p>
                      </Show>
                    )}
                    <button
                      type="button"
                      class="w-full rounded-md bg-v2-background-bg-contrast px-3 py-2 text-[12px] font-semibold text-v2-text-text-contrast disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={
                        template.kind === "preview" ||
                        props.disabled ||
                        (template.id === "account-card" && props.accountCardNeedsPeriod)
                      }
                      aria-label={language.t("cowork.enterprise.reportTemplates.launch.generateLabel", {
                        template: language.t(template.titleKey),
                      })}
                      aria-describedby={template.kind === "preview" ? undefined : props.describedBy}
                      aria-busy={Boolean(props.busyTemplateID && props.busyTemplateID === template.id)}
                      onClick={() => {
                        if (template.kind === "accounting") props.onGenerate(template)
                      }}
                    >
                      {language.t("cowork.agent.settings.templates.generate")}
                    </button>
                  </div>
                </article>
              )}
            </For>
          </div>
        </Show>
      </section>
    </div>
  )
}
